import React from 'react';
import { graphql, useFragment } from 'react-relay';
import type { GoalsCell$key } from '@atlassian/jira-relay/src/__generated__/GoalsCell.graphql';
import { GoalsInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-goals-inline-edit-full/src/ui/goals/index.tsx';
import {
	useIsSingleLineRowHeightEnabled,
	useIsInlineEditingExtendedFieldSupportEnabled,
} from '../../../../controllers/features/selectors.tsx';

import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';

import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';

export type Props = {
	fieldRef: GoalsCell$key;
	isReadOnlyWithoutHoverPopover?: boolean;
};

export const GoalsCell = ({ fieldRef, isReadOnlyWithoutHoverPopover }: Props) => {
	const data = useFragment<GoalsCell$key>(
		graphql`
			fragment GoalsCell on JiraGoalsField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...goals_issueFieldGoalsInlineEditFull_GoalsInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);
	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();

	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data &&
		isInlineEditingExtendedFieldSupportEnabled &&
		data.lazyIsEditableInIssueView;

	useLazyLoadEditability(data.id, isEditable);
	const shouldTruncate = useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;

	return (
		<GoalsInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={(isEditable && !isReadOnlyWithoutHoverPopover) ?? false}
			menuPosition="fixed"
			readViewFitContainerHeight
			isTruncated={shouldTruncate}
		/>
	);
};
